import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { cx } from '../cx';
import {
  LOGO_FACE_OPACITY,
  LOGO_LIT_FILL,
  LOGO_TLD_OPACITY,
  LOGO_VIEW_BOX,
  LOGO_WORDMARK_PATHS,
  LOGOMARK_PATHS,
} from '../tokens/logo';

// The developerz.ai mark as inline SVG, drawn from the one geometry source
// (`tokens/logo.ts`). Every path paints `currentColor`, so the lockup takes the
// parent's CSS `color` and inverts with the theme; the lit face alone paints the
// `working` accent. `full` is the cube + wordmark lockup, `icon` the cube alone.
// The lit face carries
// `dz-logo__lit` so a theme stylesheet can repoint its fill (CSS beats the
// presentation attribute). Accessible name: `role="img"` + `aria-label`; the
// lockup IS the brand name, so no sibling text repeats it.
export type LogoVariant = 'full' | 'icon';

/**
 * The brand name, and so the mark's accessible name. A proper noun: identical
 * in every locale, which is why it is a constant here and not caller copy like
 * the rest of `Nav`/`Footer`.
 *
 * Exported because a LINK whose only content is this mark has to name ITSELF.
 * Browsers do resolve `<a><svg role="img" aria-label="…"></a>` by descending
 * into the child (accname "name from content"), but a name that depends on that
 * traversal is invisible to anything reading `textContent` — `Nav`'s and
 * `Footer`'s brand links therefore carry their own `aria-label`, from here, so
 * there is one string and not three.
 */
export const LOGO_LABEL = 'developerz.ai';

export type LogoProps = {
  readonly variant?: LogoVariant;
  /** Rendered height in px; width follows the variant's viewBox. */
  readonly height?: number;
  readonly class?: string;
};

const DEFAULT_HEIGHT = 24;

export function Logo(props: LogoProps): JSX.Element {
  const variant = (): LogoVariant => props.variant ?? 'full';
  const viewBox = (): string => LOGO_VIEW_BOX[variant()];
  const height = (): number => props.height ?? DEFAULT_HEIGHT;
  const width = (): number => {
    const [, , w, h] = viewBox().split(' ').map(Number);
    return Math.round(((height() * (w ?? 0)) / (h ?? 1)) * 100) / 100;
  };
  return (
    <svg
      class={cx('dz-logo', `dz-logo--${variant()}`, props.class)}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox()}
      width={width()}
      height={height()}
      role="img"
      aria-label={LOGO_LABEL}
    >
      <path fill="currentColor" fill-opacity={LOGO_FACE_OPACITY} d={LOGOMARK_PATHS.left} />
      <path fill="currentColor" fill-opacity={LOGO_FACE_OPACITY} d={LOGOMARK_PATHS.right} />
      <path class="dz-logo__lit" fill={LOGO_LIT_FILL} d={LOGOMARK_PATHS.lit} />
      <Show when={variant() === 'full'}>
        <path fill="currentColor" d={LOGO_WORDMARK_PATHS.name} />
        <path fill="currentColor" fill-opacity={LOGO_TLD_OPACITY} d={LOGO_WORDMARK_PATHS.tld} />
      </Show>
    </svg>
  );
}
